// Modules
import React from 'react'
import { useTracking } from 'react-tracking'
import { ErrorResponse } from 'apollo-link-error'

// Application
import { ChangePasswordInfoInput } from 'typings'
import {
    Analytics,
    OutputErrorMsg,
    OutputErrorCode,
    STORAGE_RECOVER_PASSWORD,
    STORAGE_RECOVER_PASSWORD_PHONE,
    STORAGE_RECOVER_PASSWORD_TOKEN,
    STORAGE_RECOVER_PASSWORD_USERNAME,
    ROUTES_PATH,
} from 'constants/index'
import { clearStorage } from 'utils'
import { useRecoverHook } from 'hooks'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'

// Components
import { NewPassword, NewPasswordInitial } from 'components/Presenters/NewPassword'
import { NewPasswordSimplified } from 'components/Presenters/NewPasswordSimplified'
import { BiometyDataType } from 'typings/unico'
import { UNICO_CONSTANTS } from 'redesign/constants'
import { useUnicoService } from 'services/unico'
import { useHistory } from 'react-router-dom'

export const NewPasswordWrapper: React.VFC = () => {
    const recover = useRecoverHook()
    const { isFeatureEnabled } = useRemoteConfig()
    const { changePasswordUnico } = useUnicoService()
    const { trackEvent } = useTracking()
    const history = useHistory()

    const biometyDataLS: BiometyDataType = JSON.parse(sessionStorage.getItem(UNICO_CONSTANTS.STORAGE)!)

    const handleSubmit = async ({ password }: NewPasswordInitial) => {
        const changePasswordInfoInput: ChangePasswordInfoInput = {
            username: recover.username,
            token: recover.token?.token,
            newPassword: password,
        }

        try {
            recover.setSubmitting(true)

            if (biometyDataLS?.origin === UNICO_CONSTANTS.ORIGIN.RECOVER && biometyDataLS.unico) {
                await changePasswordUnico(biometyDataLS.federalTaxId!, biometyDataLS.id!, password)
                sessionStorage.setItem(
                    UNICO_CONSTANTS.STORAGE,
                    JSON.stringify({
                        ...biometyDataLS,
                        isPasswordChanged: true,
                    }),
                )
                history.push(ROUTES_PATH.FACE_BIOMETRICS_SCREEN)
                return
            }

            await recover.changePassword(changePasswordInfoInput)
            clearStorage([
                STORAGE_RECOVER_PASSWORD_USERNAME,
                STORAGE_RECOVER_PASSWORD_PHONE,
                STORAGE_RECOVER_PASSWORD_TOKEN,
                STORAGE_RECOVER_PASSWORD,
                UNICO_CONSTANTS.STORAGE,
            ])
            recover.resetRecoverPasswordState()
            trackEvent(Analytics.RecoverPasswordSteps.NewPasswordOnSubmit)
            recover.nextStep(changePasswordInfoInput)
        } catch (error) {
            const e: ErrorResponse = error
            if (e.graphQLErrors) {
                const codeError = e.graphQLErrors[0].extensions?.code
                if (
                    codeError === OutputErrorCode.INVALIDTOKEN ||
                    codeError === OutputErrorCode.EXPIREDTOKEN ||
                    codeError === OutputErrorCode.BAD_USER_INPUT
                ) {
                    recover.moveToError('/recuperar-senha/token', {
                        status: 'error',
                        message:
                            codeError === OutputErrorCode.INVALIDTOKEN ? OutputErrorMsg.INVALIDTOKEN : OutputErrorMsg.EXPIREDTOKEN,
                    })
                }
            }
        } finally {
            recover.setSubmitting(false)
        }
    }

    const isPasswordSimplifiedActivated = isFeatureEnabled('AUTH_PASSWORD_SIMPLIFICATION')
    const initialValues = { password: '', confirmPassword: '' }

    return isPasswordSimplifiedActivated ? (
        <NewPasswordSimplified handleSubmit={handleSubmit} initialValues={initialValues} />
    ) : (
        <NewPassword handleSubmit={handleSubmit} initialValues={initialValues} />
    )
}
